// ─────────────────────────────────────────────────────────────────────────────
// SecurityPage, the public /security page.
//
// The argument of the page is that a record of where the money went is only
// worth keeping if nobody can quietly change it. So the page is ordered as:
// where the software runs, what cannot be overwritten, who can see what, the
// whole control set in one spec list, and then, unusually, an honest list of
// what has not been done yet.
//
// Everything stated here is something the product actually does. No audit,
// certification, penetration test, bug bounty or uptime figure is claimed,
// because none of those exist. The candour section is the point of the page.
//
// Monochrome, one dark panel, one captioned <MediaSlot> standing in for the
// architecture diagram that has not been drawn yet.
// ─────────────────────────────────────────────────────────────────────────────
import { Link } from 'react-router-dom';
import {
    BTN_SECONDARY,
    Icon,
    Container,
    Section,
    Heading,
    Lead,
    Body,
    Key,
    Eyebrow,
    MediaSlot,
    SectionHead,
    FeatureRow,
    FeatureGrid,
    StatBand,
    SpecList,
    PageHero,
    CTASection,
    MarketingShell,
    Seo,
} from '../../components/marketing/MarketingKit';
import { Reveal } from '../../components/marketing/Motion';

// ── The four things that are never edited in place ───────────────────────────
const IMMUTABLE = [
    {
        title: 'The inventory ledger only appends',
        body: 'Stock movements are written as deltas. On hand is derived by reading them back rather than stored as a number, so a bad count is corrected by another movement instead of by typing over the first one.',
    },
    {
        title: 'Invoices are credited, never deleted',
        body: 'A wholesale invoice that was wrong keeps its number and carries a credit against it. The balance changes, the history does not, and the account still adds up a year later.',
    },
    {
        title: 'Manual changes are logged permanently',
        body: 'Who changed what, when, and from where. The audit log is written by the system as changes happen, and there is no screen in the dashboard that edits or removes an entry.',
    },
    {
        title: 'Dashboard edits land beside the raw record',
        body: 'Corrections and overrides are written to their own layer. The imported record underneath keeps the values it arrived with, so you can always read what the source actually said.',
    },
];

// ── Access control ───────────────────────────────────────────────────────────
const ACCESS = [
    {
        icon: 'shield',
        title: 'Role-based access',
        body: 'Permissions cover 20 modules. Eight presets cover the usual jobs, and any preset can be narrowed further for one person without inventing a new role.',
    },
    {
        icon: 'layers',
        title: 'Workspace isolation',
        body: 'Every read and every write is scoped to a single workspace. Running more than one business from one login does not let either of them see the other.',
    },
    {
        icon: 'pin',
        title: 'Scoped to a location',
        body: 'A manager can be held to the stores they actually run, so the rollup for every other location is not one click away from their dashboard.',
    },
    {
        icon: 'lock',
        title: 'Single-use staff codes',
        body: 'A temporary sign-in code works once and expires ten minutes after it is issued. There is nothing left to reuse and nothing worth passing around.',
    },
    {
        icon: 'users',
        title: 'People, not shared logins',
        body: 'Collaborators are invited by email address and sign in as themselves, so the audit log names a person rather than an office account everybody knows.',
    },
    {
        icon: 'search',
        title: 'View as a role',
        body: 'Check what a manager can actually see by looking through their permissions yourself. The banner stays on screen for as long as you are doing it.',
    },
];

// ── The whole control set, in the order a reviewer reads it ──────────────────
const CONTROLS = [
    { label: 'Hosting', value: 'Amazon Web Services' },
    { label: 'Application layer', value: 'API Gateway and Lambda' },
    { label: 'Data store', value: 'DynamoDB' },
    { label: 'Authentication', value: 'AWS Cognito, verified by emailed code' },
    { label: 'Sessions', value: 'Tokens refreshed in the background' },
    { label: 'Encryption', value: 'In transit and at rest' },
    { label: 'Secrets', value: 'Held server side, never in the browser bundle' },
    { label: 'Access control', value: '20 modules, 8 role presets' },
    { label: 'Workspace isolation', value: 'Every read and write scoped to one workspace' },
    { label: 'Staff sign-in codes', value: 'Single use, expire in ten minutes' },
    { label: 'Audit log', value: 'Permanent record of manual changes' },
    { label: 'Inventory records', value: 'Append only, on hand derived' },
    { label: 'Invoices', value: 'Credited, never deleted' },
    { label: 'Dashboard edits', value: 'Written to an overrides layer' },
    { label: 'Data export', value: 'CSV, Excel and PDF from every table' },
];

// ── The part most security pages leave out ───────────────────────────────────
const NOT_YET = [
    {
        title: 'No SOC 2 report',
        body: 'We have not been through a SOC 2 audit. When we have, the report and its date will be on this page. Until then, saying so is more useful to you than a badge that means nothing.',
    },
    {
        title: 'No ISO 27001 certificate',
        body: 'Nothing here has been certified by a third party. If your procurement process requires a certificate before you can sign, we are not there yet and we will say so on the first call.',
    },
    {
        title: 'No penetration test to send',
        body: 'We have not commissioned an external test, so there is no report to attach to a questionnaire. We can walk you through the architecture and the access model instead.',
    },
    {
        title: 'No bug bounty',
        body: 'There is no paid program and no submission portal. If you find something, write to us and a person who can fix it will answer you directly.',
    },
    {
        title: 'No uptime figure',
        body: 'We will not print a percentage we cannot show the working for. The status page reports the current state of the platform rather than a headline number.',
    },
    {
        title: 'No identity provider sign-on',
        body: 'Signing in today means an email address and a password held by Cognito, with a verification code on a new account. There is no directory or single sign-on option to offer you yet.',
    },
];

// ── Page ─────────────────────────────────────────────────────────────────────
export default function SecurityPage() {
    return (
        <MarketingShell>
            <Seo
                title="Security and access control"
                description="How Wavelength is built and secured: AWS hosting, Cognito sign-in, role-based access across 20 modules, an append-only ledger and a permanent audit log."
                path="/security"
                keywords="retail back office security, role based access control, audit log software, append only ledger, AWS Cognito authentication, multi location permissions, workspace data isolation, security questionnaire"
                jsonLd={{
                    '@context': 'https://schema.org',
                    '@graph': [
                        {
                            '@type': 'WebPage',
                            '@id': 'https://wavelengtherp.com/security#webpage',
                            url: 'https://wavelengtherp.com/security',
                            name: 'Security and access control',
                            description: 'Architecture, authentication, role-based access, the append-only ledger and the audit log behind Wavelength, with an honest list of what has not been done yet.',
                            publisher: { '@id': 'https://wavelengtherp.com/#organization' },
                        },
                        {
                            '@type': 'BreadcrumbList',
                            itemListElement: [
                                { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://wavelengtherp.com/' },
                                { '@type': 'ListItem', position: 2, name: 'Security', item: 'https://wavelengtherp.com/security' },
                            ],
                        },
                    ],
                }}
            />

            {/* ── Hero ───────────────────────────────────────────────────── */}
            <PageHero
                eyebrow="Security"
                title="Nothing here can be quietly changed."
                lead={
                    <>
                        Wavelength holds the record of where your money went. That record is only worth keeping if
                        nobody can rewrite it afterwards, so the ledger <Key>only ever appends</Key>, invoices are
                        credited rather than deleted, and every manual change is written down with the name of the
                        person who made it.
                    </>
                }
                primary={{ label: 'Book a walkthrough', to: '/contact' }}
                secondary={{ label: 'See the platform', to: '/features' }}
            >
                <Reveal>
                    <StatBand
                        countUp
                        stats={[
                            { value: '20', label: 'Modules under role control', note: 'Every screen a person can reach is one of them' },
                            { value: '8', label: 'Role presets', note: 'Applied per person, then narrowed by hand' },
                            { value: '10 min', label: 'Life of a staff sign-in code', note: 'Single use, and dead the moment it is used' },
                            { value: '0', label: 'Secrets shipped to the browser', note: 'Keys and credentials stay on the server' },
                        ]}
                    />
                </Reveal>
            </PageHero>

            {/* ── Where it runs ──────────────────────────────────────────── */}
            <Section tone="paper2" pad="lg">
                <Container>
                    <Reveal>
                        <FeatureRow
                            eyebrow="Architecture"
                            title="Managed infrastructure, not a box in the back room."
                            body="There is no server in your office to patch and nothing for a store to lose in a flood. The workspace runs on Amazon Web Services, and the parts that hold your data are managed services rather than machines somebody has to remember."
                            bullets={[
                                'Requests arrive at API Gateway, are served by Lambda functions and are stored in DynamoDB',
                                'Sign-in runs through AWS Cognito, with a new account verified by a code sent to its own email address',
                                'Sessions refresh their own tokens in the background rather than dropping a busy counter back at the sign-in screen',
                                'Traffic is encrypted in transit and records are encrypted at rest',
                                'Keys and credentials are held server side and are never compiled into the browser bundle',
                            ]}
                            media={
                                <MediaSlot
                                    kind="Diagram"
                                    label="The request path, drawn end to end"
                                    note="Browser to API Gateway to Lambda to DynamoDB, with the Cognito token checked before anything is read"
                                    ratio="4 / 3"
                                    className="!rounded-[28px]"
                                />
                            }
                        />
                    </Reveal>
                </Container>
            </Section>

            {/* ── Dark panel: the append-only argument ───────────────────── */}
            <Section pad="sm">
                <Container>
                    <Reveal>
                        <div className="relative overflow-hidden rounded-[28px] bg-[#111111] p-6 text-white sm:p-12 lg:p-14">
                            <div className="flex max-w-[560px] flex-col gap-5">
                                <Eyebrow className="!text-white/45">The record</Eyebrow>
                                <h2 className="text-[28px] font-semibold leading-[1.08] tracking-[-0.025em] text-white sm:text-[36px]">
                                    Nothing is overwritten.
                                </h2>
                                <p className="text-[16px] leading-[1.6] text-white/60">
                                    Most back-office software treats a correction as an edit. The number was wrong, it
                                    is now right, and what it used to say is gone. That is exactly the moment a trail
                                    stops being evidence, so Wavelength keeps both.
                                </p>
                            </div>

                            <div className="mt-10 grid grid-cols-1 gap-px overflow-hidden rounded-[24px] bg-white/[0.14] sm:grid-cols-2">
                                {IMMUTABLE.map((it) => (
                                    <div key={it.title} className="flex flex-col gap-2.5 bg-[#111111] p-6 sm:p-7">
                                        <h3 className="text-[17px] font-semibold tracking-[-0.015em] text-white sm:text-[19px]">
                                            {it.title}
                                        </h3>
                                        <p className="text-[14px] leading-[1.65] text-white/55">{it.body}</p>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </Reveal>
                </Container>
            </Section>

            {/* ── Access control ─────────────────────────────────────────── */}
            <Section tone="paper2" pad="lg">
                <Container>
                    <Reveal>
                        <SectionHead
                            title="Who can see what."
                            lead="A back office holds payroll-adjacent numbers, supplier prices and every variance a person has ever been responsible for. Access is set per person, per module and per location, and it is set once rather than argued about every week."
                            maxWidth="max-w-[540px]"
                        />
                    </Reveal>
                    <Reveal delay={80}>
                        <FeatureGrid className="mt-12" items={ACCESS} cols={3} />
                    </Reveal>
                </Container>
            </Section>

            {/* ── The control set ────────────────────────────────────────── */}
            <Section pad="lg">
                <Container>
                    <div className="grid grid-cols-1 gap-10 lg:grid-cols-[340px_1fr] lg:gap-16">
                        <Reveal>
                            <div className="flex flex-col gap-4 lg:sticky lg:top-28">
                                <Heading>The controls, in one list.</Heading>
                                <Lead>
                                    Everything a review usually asks for, in the order it usually gets asked. If your
                                    questionnaire has a row this list does not cover, send it and we will fill it in.
                                </Lead>
                                <div className="mt-2">
                                    <Link to="/contact" className={BTN_SECONDARY}>
                                        Send us a questionnaire
                                        <Icon name="arrow" className="h-4 w-4" />
                                    </Link>
                                </div>
                            </div>
                        </Reveal>
                        <Reveal delay={80}>
                            <SpecList rows={CONTROLS} />
                        </Reveal>
                    </div>
                </Container>
            </Section>

            {/* ── Candour ────────────────────────────────────────────────── */}
            <Section tone="paper2" pad="lg">
                <Container>
                    <Reveal>
                        <SectionHead
                            title="What we have not done yet."
                            lead="Security pages are usually a wall of badges. Here is the other half of ours, written down so you do not have to work it out during procurement."
                            maxWidth="max-w-[540px]"
                        />
                    </Reveal>
                    <Reveal delay={80}>
                        <FeatureGrid className="mt-12" items={NOT_YET} cols={3} numbered />
                    </Reveal>
                    <Reveal delay={120}>
                        <div className="mt-4 rounded-[24px] border border-[rgba(10,10,10,0.10)] bg-white p-6 sm:p-8">
                            <Body className="max-w-[720px] text-[15px]">
                                None of this is on a roadmap slide with a quarter next to it. When one of these
                                changes, this page changes with it and the old line goes away. If your review needs
                                something that is not here, ask for it directly and you will get a straight answer,
                                including the answers that are still no.
                            </Body>
                        </div>
                    </Reveal>
                </Container>
            </Section>

            {/* ── Closing CTA ────────────────────────────────────────────── */}
            <CTASection
                eyebrow="Security"
                title="Read the log before you trust the number."
                body="Book a walkthrough and we will open the audit log, the movement ledger and the permission matrix on a live workspace, including the parts still on the list above."
                primary={{ label: 'Book a walkthrough', to: '/contact' }}
                secondary={{ label: 'See the platform', to: '/features' }}
            />
        </MarketingShell>
    );
}
